//======================================================================================================
//
// This store houses all user information and will update the application based on role and authentication.
//
//======================================================================================================

import { defineStore } from "pinia";
import { useApiStore as api } from "./apiStore";
import { useSiteStore as siteStore } from "./siteStore";
import { usePvwStore as pvw } from "./ssaStore/pvw";

import { CustomErrorCodes } from "@/utils/CustomErrorCodes";
import axios, { AxiosResponse } from "axios";

import i18n from "@/utils/i18n";

export const useAuthStore = defineStore("authStore", {
	state: () =>
		({
			user: {},
			timeoutInterval: null as any
		}) as AuthState,
	actions: {
		setTimeoutInterval() {
			const timeoutInterval = setInterval(() => {
				pvw().PVW_APPLICATION_STATE_GET();
			}, 35000);
			clearTimeout(this.timeoutInterval);
			this.timeoutInterval = timeoutInterval;
		},
		setUser(payload: AuthUser) {
			this.user = payload;
		},
		async logout() {
			localStorage.removeItem("mineportal-oauth-token");
			localStorage.removeItem("mineportal-auth-token");
			localStorage.removeItem("mineportal-active-site");
			localStorage.removeItem("sessionUrl");
			delete axios.defaults.headers.common["Authorization"];
			await this.auth0.logout({ returnTo: window.location.origin });
		},
		async login(payload: { email: string; password: string } | null) {
			localStorage.removeItem("wslinkStore");
			localStorage.removeItem("layerStore");
			localStorage.removeItem("viewStore");
			localStorage.removeItem("activeStore");
			localStorage.removeItem("presetsStore");
			localStorage.removeItem("displayPaneStore");
			localStorage.removeItem("pvwStore");
			localStorage.removeItem("hierarchyStore");
			localStorage.removeItem("sessionUrl");
			localStorage.removeItem("sessionSecret");

			this.setTimeoutInterval();
			return new Promise((resolve, reject) => {
				if (payload == null) {
					siteStore()
						.init()
						.then(() => {
							resolve(this.auth0.loginWithRedirect());
						});
				} else {
					// eslint-disable-next-line
					api()
						.post({
							endpoint: "/user/login",
							post: { email: payload.email, password: payload.password }
						})
						.then(
							async (res: AxiosResponse) => {
								localStorage.setItem("mineportal-auth-token", res.data.token);
								localStorage.removeItem("mineportal-oauth-token");
								api().setApiToken(res.data.token);
								api().getApiInstance().defaults.headers.common["Authorization"] =
									"Bearer " + res.data.token;
								try {
									// eslint-disable-next-line
									const { data: me }: { data: AuthUser } = await api().get(
										"/user/read/me?includeUserPreferences=true"
									);
									this.setUser(me);

									if (me.userPreferences.language) {
										i18n.global.locale = me.userPreferences.language as
											| "en"
											| "es"; // this is just a hint, it doesn't enforce anything, but it should match the languages in mineportal-core-ui\src\utils\i18n\index.ts
									}

									if (me.isActive) {
										await siteStore().init();

										if (!siteStore().sites.length) {
											throw new Error(CustomErrorCodes.SiteMissing);
										}

										if (me.userPreferences.defaultSite == null) {
											if (siteStore().sites[0].id != null) {
												siteStore().SET_ACTIVE_SITE(
													siteStore().sites[0].id
												);
											}
										} else {
											siteStore().SET_ACTIVE_SITE(
												me.userPreferences.defaultSite
											);
										}

										this.router.push("/ssa");
									} else if (me.isActive === false) {
										// Deactivated
										this.router.push("/request-access");
									} else {
										// Pending (usually wouldn't get here - user should activate
										// via email link)
										this.router.push("/activate-account");
									}
								} catch (e) {
									reject(e);
								}
							},
							(e: any) => {
								reject(e);
							}
						);
				}
			});
		}
	}
});
